import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { consumePkceVerifier, createPkceChallenge, getCognitoRedirectUri } from '../lib/cognitoPkce';

// User role type
export type UserRole = 'ADMIN' | 'MANAGER' | 'HR' | 'EMPLOYEE';

interface User {
  id: string;
  email: string;
  tenant_id?: string;
  modules?: string[];
  can_invite_users?: boolean;
  first_name: string;
  last_name: string;
  job_title?: string;
  department?: string;
  phone?: string;
  avatar_url?: string;
  is_active: boolean;
  is_admin: boolean;
  role: UserRole;
  language: string;
  timezone: string;
  date_format: string;
  time_format: string;
  theme: string;
  email_notifications: boolean;
  push_notifications: boolean;
  leave_reminders: boolean;
  payroll_alerts: boolean;
  performance_updates: boolean;
  shift_changes: boolean;
  weekly_digest: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Role helper properties
  isAdmin: boolean;
  isManager: boolean;
  isHR: boolean;
  canApproveLeaves: boolean;
  canApproveShiftSwaps: boolean;
  // Methods
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (data: Partial<User>) => Promise<void>;
}

const persistTenant = (user?: User | null) => {
  if (user?.tenant_id) {
    localStorage.setItem('tenant_id', user.tenant_id);
    localStorage.setItem('selected_tenant_id', user.tenant_id);
  }
};

interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  department?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const env = (import.meta as any).env;
const API_BASE_URL = env.VITE_API_URL ||
  (env.REACT_APP_BACKEND_URL ? `${env.REACT_APP_BACKEND_URL}/api` : '/api');

export const getCrmAuthUrl = (path: string = '/login') => {
  const crmUrl = (import.meta as any).env.VITE_CRM_URL;
  if (crmUrl) return `${crmUrl.replace(/\/$/, '')}${path}`;
  const { hostname, protocol } = window.location;
  if (hostname.includes('dealbox-')) {
    const crmHost = hostname.replace(/dealbox-[a-z]+-/, 'dealbox-crm-');
    return `${protocol}//${crmHost}${path}`;
  }
  return `http://${hostname}:3000${path}`;
};

const cognitoLoginUrl = async () => {
  const domain = (import.meta as any).env.VITE_COGNITO_DOMAIN;
  const clientId = (import.meta as any).env.VITE_COGNITO_APP_CLIENT_ID;
  if (!domain || !clientId) return null;
  const { challenge } = await createPkceChallenge();
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: getCognitoRedirectUri(),
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  return `${domain.replace(/\/$/, '')}/login?${params.toString()}`;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('auth_user');

    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    verifyToken();
  }, []);

  const establishSessionFromHash = async () => {
    const query = new URLSearchParams(window.location.search);
    const code = query.get('code');
    if (code) {
      const codeVerifier = consumePkceVerifier();
      if (!codeVerifier) throw new Error('Missing Cognito PKCE verifier');
      window.history.replaceState(null, document.title, window.location.pathname);
      const response = await fetch(`${API_BASE_URL}/auth/session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: getCognitoRedirectUri(), code_verifier: codeVerifier }),
      });
      if (!response.ok) return false;
      const session = await response.json();
      setUser(session.user);
      localStorage.setItem('auth_user', JSON.stringify(session.user));
      persistTenant(session.user);
      return true;
    }

    return false;
  };

  const verifyToken = async () => {
    try {
      const established = await establishSessionFromHash();
      if (established) return;
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json();

        // ------------- MODULE ACCESS CHECK -------------
        try {
          const crmApiUrl = (import.meta as any).env.VITE_CRM_API_URL || `http://${window.location.hostname}:8000`;
          const permsRes = await fetch(`${crmApiUrl}/api/permissions/me`, {
            credentials: 'include'
          });
          if (permsRes.ok) {
            const permsData = await permsRes.json();
            const roleName = permsData.roleName?.toLowerCase();
            const isElevated = roleName === 'admin' || roleName === 'manager' || roleName === 'sales_manager';
            
            let hasAccess = isElevated; // fallback
            let allowedModules = { crm: isElevated, inventory: isElevated, hr: isElevated };

            const modulePerms = permsData.permissions?.find((p: any) => p.feature === 'hr');
            const inventoryPerms = permsData.permissions?.find((p: any) => p.feature === 'inventory');
            const crmPerms = permsData.permissions?.find((p: any) => p.feature === 'crm');

            allowedModules = {
               crm: crmPerms?.permissions?.accessCRM ?? isElevated,
               inventory: inventoryPerms?.permissions?.accessInventory ?? isElevated,
               hr: modulePerms?.permissions?.accessHR ?? isElevated,
            };

            // Store entire granular permission structure
            const granularPerms: Record<string, any> = {};
            permsData.permissions?.forEach((p: any) => {
               granularPerms[p.feature] = p.permissions;
            });
            localStorage.setItem('modular_permissions', JSON.stringify(granularPerms));
            if (roleName) localStorage.setItem('rbac_role', roleName);

            hasAccess = allowedModules.hr;

            localStorage.setItem('allowed_modules', JSON.stringify(allowedModules));

            if (!hasAccess && roleName !== 'admin') {
               // Token valid but no permission to view HR module
               logout();
               return;
            }
          }
        } catch (err) {
          console.warn("Failed to verify module permissions", err);
        }
        // ------------- END MODULE ACCESS CHECK -------------

        setUser(userData);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        persistTenant(userData);
      } else {
        // Token is invalid, clear storage
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    if ((import.meta as any).env.VITE_USE_COGNITO_HOSTED_LOGIN === 'true') {
      const hostedLogin = await cognitoLoginUrl();
      if (!hostedLogin) throw new Error('Cognito hosted sign-in is not configured.');
      window.location.href = hostedLogin;
      return;
    }
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    const authToken = data.access_token;

    // ---- MODULE ACCESS CHECK on login ----
    try {
      const crmApiUrl = (import.meta as any).env.VITE_CRM_API_URL || `http://${window.location.hostname}:8000`;
      const permsRes = await fetch(`${crmApiUrl}/api/permissions/me`, {
        credentials: 'include'
      });
      if (permsRes.ok) {
        const permsData = await permsRes.json();
        const roleName = permsData.roleName?.toLowerCase();
        // Admins always bypass
        if (roleName !== 'admin') {
          const hrPerms = permsData.permissions?.find((p: any) => p.feature === 'hr');
          const hasHRAccess = hrPerms?.permissions?.accessHR;
          // If explicitly false, deny access
          if (hasHRAccess === false) {
            throw new Error("You don't have permission to access the HR module.");
          }
        }

        // Store modular permissions for sidebar filtering
        const granularPerms: Record<string, any> = {};
        permsData.permissions?.forEach((p: any) => {
          granularPerms[p.feature] = p.permissions;
        });
        localStorage.setItem('modular_permissions', JSON.stringify(granularPerms));
        if (roleName) localStorage.setItem('rbac_role', roleName);
      }
    } catch (err: any) {
      if (err.message?.includes('permission')) throw err;
      console.warn('Failed to verify HR module access on login:', err);
    }
    // ---- END MODULE ACCESS CHECK ----

    setToken(authToken);
    setUser(data.user);
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    persistTenant(data.user);
  };

  const register = async (registerData: RegisterData) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(registerData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    setToken(null);
    setUser(data.user);
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    persistTenant(data.user);
  };

  const logout = () => {
    fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => undefined);
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_user');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('selected_tenant_id');
    localStorage.removeItem('modular_permissions');
    localStorage.removeItem('allowed_modules');
    sessionStorage.removeItem('cognito_pkce_verifier');

    // Redirect to CRM login (CRM is the sole identity provider)
    window.location.href = getCrmAuthUrl('/login');
  };

  const updateUser = async (data: Partial<User>) => {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Update failed');
    }

    const updatedUser = await response.json();
    setUser(updatedUser);
    localStorage.setItem('auth_user', JSON.stringify(updatedUser));
    persistTenant(updatedUser);
  };

  // Role helper computed properties - normalize to uppercase for comparison
  const userRole = (user?.role || 'EMPLOYEE').toUpperCase();
  const isAdmin = userRole === 'ADMIN';
  const isManager = userRole === 'MANAGER' || userRole === 'ADMIN';
  const isHR = userRole === 'HR' || userRole === 'ADMIN';
  const canApproveLeaves = isManager || isHR; // Managers, HR, and Admins can approve leaves
  const canApproveShiftSwaps = isManager; // Only Managers and Admins can approve shift swaps

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        isAdmin,
        isManager,
        isHR,
        canApproveLeaves,
        canApproveShiftSwaps,
        login,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook to access user preferences
 * Returns user's date format, time format, and timezone preferences
 */
export const usePreferences = () => {
  const { user } = useAuth();

  return {
    dateFormat: user?.date_format || 'MM/DD/YYYY',
    timeFormat: user?.time_format || '12h',
    timezone: user?.timezone || 'America/New_York',
    language: user?.language || 'en',
    theme: user?.theme || 'system'
  };
};

export default AuthContext;
